import React, { useEffect, useMemo } from 'react';
import {
  useDraftSitrepsV2,
  useSitrepsPendingReview,
  useSitrepsV2,
} from 'components/DisasterGPT/hooks/useSitrepsV2';
import {
  useReportsFilterOptions,
  type ReportsFilterSelection,
} from 'components/Reports/hooks/useReportsFilterOptions';
import { useScopedSitrepViewData } from 'components/Reports/hooks/useScopedSitrepViewData';
import { useAllIncidents } from 'components/IAP/hooks/incidentHooks';
import ReportsExportButton from 'components/Reports/ReportsExportButton';
import ReportsFilterBar from 'components/Reports/ReportsFilterBar';
import { useAors } from 'hooks/useAors';
import 'styles/pages/_reports.scss';
import ReportsSitrepView from 'components/Reports/ReportsSitrepView';
import { selectIncident, useAppSelector } from 'slices/commonSelectors';
import { useLocation, useParams, useSearchParams } from 'react-router';

const ReportsView: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const incidentId = searchParams.get('incidentId');
  const aorId = searchParams.get('aorId');

  const {
    data: reports = [],
    isLoading: isLoadingReports,
    isError: isErrorReports,
    refetch: refetchReports,
  } = useSitrepsV2();
  const {
    data: draftSitreps = [],
    isLoading: isLoadingDrafts,
    isError: isErrorDrafts,
    refetch: refetchDrafts,
  } = useDraftSitrepsV2();
  const {
    data: sitrepsPendingReview = [],
    isLoading: isLoadingPendingReviews,
    isError: isErrorPendingReviews,
    refetch: refetchPendingReviews,
  } = useSitrepsPendingReview();

  // Get AORs and Incidents for filter name
  const { data: aors = [] } = useAors();
  const { data: incidents = [] } = useAllIncidents();
  const currentIncident = useAppSelector(selectIncident);
  const location = useLocation();
  const { incidentId: pathIncidentId } = useParams<{ incidentId?: string }>();

  const isInIAPContext = useMemo(() => {
    return location.pathname.includes('/iap/') && !!currentIncident;
  }, [currentIncident, location.pathname]);

  useEffect(() => {
    if (!isInIAPContext || !currentIncident) {
      return;
    }

    // Mid-workspace-switch the pathname still points at the old incident;
    // setSearchParams rebuilds the URL from the CURRENT pathname, so writing
    // now would replace the URL back onto the old /iap/<id> segment and undo
    // the switch. Wait until the path and Redux agree, then reconcile.
    if (pathIncidentId && pathIncidentId !== currentIncident.id) {
      return;
    }

    if (incidentId === currentIncident.id && !aorId) {
      return;
    }

    setSearchParams(
      (currentSearchParams) => {
        currentSearchParams.set('incidentId', currentIncident.id);
        currentSearchParams.delete('aorId');
        return currentSearchParams;
      },
      { replace: true }
    );
  }, [
    aorId,
    currentIncident,
    incidentId,
    isInIAPContext,
    pathIncidentId,
    setSearchParams,
  ]);

  const selectedIncident = useMemo(() => {
    const effectiveIncidentId =
      incidentId ?? (isInIAPContext ? currentIncident?.id : null);

    if (!effectiveIncidentId) {
      return null;
    }

    return incidents.find((i) => i.id === effectiveIncidentId) || null;
  }, [currentIncident?.id, incidentId, incidents, isInIAPContext]);

  const selectedAorId = useMemo(() => {
    return aorId ? (aorId as UUID) : null;
  }, [aorId]);

  const clearSelectedFilter = () => {
    setSearchParams(
      (searchParams) => {
        searchParams.delete('aorId');
        searchParams.delete('incidentId');
        return searchParams;
      },
      { replace: true }
    );
  };

  const selectedFilter = useMemo<ReportsFilterSelection | null>(() => {
    if (selectedIncident) {
      return {
        value: selectedIncident.id,
        label: selectedIncident.name,
        kind: 'incident',
      };
    }

    if (selectedAorId) {
      const selectedAor = aors.find((aor) => aor.id === selectedAorId);

      if (selectedAor) {
        return {
          value: selectedAor.id,
          label: selectedAor.name,
          kind: 'aor',
        };
      }
    }

    return null;
  }, [aors, selectedAorId, selectedIncident]);

  const { groupedOptions, selectedOption } = useReportsFilterOptions({
    aors,
    incidents,
    selectedFilter,
  });

  const handleSelectedFilterChange = (
    nextSelectedFilter: ReportsFilterSelection | null
  ) => {
    if (!nextSelectedFilter) {
      clearSelectedFilter();
      return;
    }

    setSearchParams(
      (nextSearchParams) => {
        if (nextSelectedFilter.kind === 'incident') {
          nextSearchParams.set('incidentId', nextSelectedFilter.value);
          nextSearchParams.delete('aorId');
        } else {
          nextSearchParams.set('aorId', nextSelectedFilter.value);
          nextSearchParams.delete('incidentId');
        }

        return nextSearchParams;
      },
      { replace: true }
    );
  };

  // Calculate the filter name based on current selection
  const filterName = selectedFilter?.label ?? null;

  const hasSelectedFilter = Boolean(selectedFilter);

  const selectedScope = useMemo(
    () => ({
      incidentId: selectedIncident?.id ?? null,
      aorId: selectedAorId,
    }),
    [selectedAorId, selectedIncident?.id]
  );

  const sitrepViewData = useMemo(
    () => ({
      publishedSitreps: {
        data: reports,
        isLoading: isLoadingReports,
        isError: isErrorReports,
        refetch: refetchReports,
      },
      draftSitreps: {
        data: draftSitreps,
        isLoading: isLoadingDrafts,
        isError: isErrorDrafts,
        refetch: refetchDrafts,
      },
      pendingReviews: {
        data: sitrepsPendingReview,
        isLoading: isLoadingPendingReviews,
        isError: isErrorPendingReviews,
        refetch: refetchPendingReviews,
      },
    }),
    [
      draftSitreps,
      isErrorDrafts,
      isErrorPendingReviews,
      isErrorReports,
      isLoadingDrafts,
      isLoadingPendingReviews,
      isLoadingReports,
      refetchDrafts,
      refetchPendingReviews,
      refetchReports,
      reports,
      sitrepsPendingReview,
    ]
  );

  const scopedSitrepViewData = useScopedSitrepViewData({
    ...sitrepViewData,
    scope: selectedScope,
  });

  if (isErrorReports) {
    return (
      <div className="mt-4 text-center">
        <span>Error loading reports.</span>
      </div>
    );
  }

  return (
    <div className="">
      <div className="reports-view__top-controls">
        <div className="reports-view__filter-bar">
          <ReportsFilterBar
            groupedOptions={groupedOptions}
            selectedFilter={selectedOption}
            onSelectedFilterChange={handleSelectedFilterChange}
            disableFilter={isInIAPContext} // Disable filter if we're already in an incident context
          />
        </div>

        <div className="reports-view__export-button">
          <ReportsExportButton
            sitreps={scopedSitrepViewData.publishedSitreps.data}
            filterName={filterName}
          />
        </div>
      </div>

      {!hasSelectedFilter ? (
        <div className="mt-4 text-center">
          <span>Please select an AOR or Incident to view reports.</span>
        </div>
      ) : (
        <div>
          <ReportsSitrepView
            scopeName={filterName || 'your organization'}
            publishedSitreps={scopedSitrepViewData.publishedSitreps}
            draftSitreps={scopedSitrepViewData.draftSitreps}
            pendingReviews={scopedSitrepViewData.pendingReviews}
            aors={aors}
            aorId={selectedIncident ? undefined : selectedAorId || undefined}
            incident={selectedIncident || undefined}
          />
        </div>
      )}
    </div>
  );
};

export default ReportsView;
